import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  Headphones, 
  Plus,
  Send,
  MessageSquare,
  Phone,
  Building2,
  Home,
  Globe,
  Copy,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  ExternalLink,
  Users,
  CreditCard,
  Activity
} from "lucide-react";
import { toast } from "sonner";

const SCENARIOS = [
  { value: "in_clinic", label: "In-Clinic Test", icon: Building2, description: "Patient at clinic, audiologist-assisted", paymentRequired: false },
  { value: "remote_link", label: "Remote Link", icon: Send, description: "Send test link via SMS/WhatsApp", paymentRequired: false },
  { value: "self_test_paid", label: "Self-Test (Paid)", icon: Globe, description: "DIY test from website with ₹499 payment", paymentRequired: true },
  { value: "home_visit", label: "Home Visit", icon: Home, description: "Audiologist at patient's home", paymentRequired: false }
];

export default function SelfTestManagementPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sending, setSending] = useState({});
  const [selectedScenario, setSelectedScenario] = useState("in_clinic");
  
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    age: "",
    gender: "",
    scenario: "in_clinic",
    notes: ""
  });

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    try {
      const response = await apiClient.get("/self-tests");
      setTests(response.data);
    } catch (error) {
      console.error("Failed to fetch tests:", error);
      toast.error("Failed to load tests");
    } finally {
      setLoading(false);
    }
  };

  const createTest = async () => {
    if (!formData.name || !formData.phone) {
      toast.error("Name and phone are required");
      return;
    }

    try {
      const response = await apiClient.post("/self-tests", {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null,
        scenario: selectedScenario
      });
      
      toast.success("Test created successfully!");
      setDialogOpen(false);
      setFormData({ name: "", phone: "", email: "", age: "", gender: "", scenario: "in_clinic", notes: "" });
      fetchTests();
      
      // Copy link to clipboard
      navigator.clipboard.writeText(response.data.test_link);
      toast.success("Test link copied to clipboard!");
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create test");
    }
  };

  const sendLink = async (testId, channel) => {
    setSending({ ...sending, [testId]: channel });
    try {
      await apiClient.post(`/self-tests/${testId}/send-link?channel=${channel}`);
      toast.success(`Link sent via ${channel}!`);
      fetchTests();
    } catch (error) {
      toast.error("Failed to send link");
    } finally {
      setSending({ ...sending, [testId]: null });
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard!");
  };

  const formatStatus = (status) => {
    const statusMap = {
      created: { label: "Created", color: "bg-slate-100 text-slate-700" },
      link_sent: { label: "Link Sent", color: "bg-blue-100 text-blue-700" },
      otp_sent: { label: "OTP Sent", color: "bg-amber-100 text-amber-700" },
      otp_verified: { label: "Verified", color: "bg-emerald-100 text-emerald-700" },
      payment_pending: { label: "Payment Pending", color: "bg-amber-100 text-amber-700" },
      payment_completed: { label: "Paid", color: "bg-green-100 text-green-700" },
      test_in_progress: { label: "Testing", color: "bg-purple-100 text-purple-700" },
      test_completed: { label: "Completed", color: "bg-emerald-100 text-emerald-700" },
      expired: { label: "Expired", color: "bg-red-100 text-red-700" }
    };
    return statusMap[status] || { label: status, color: "bg-slate-100 text-slate-700" };
  };

  const getScenarioIcon = (scenario) => {
    const icons = {
      in_clinic: Building2,
      remote_link: Send,
      self_test_paid: Globe,
      home_visit: Home
    };
    return icons[scenario] || Globe;
  };

  // Stats
  const stats = {
    total: tests.length,
    active: tests.filter(t => !["test_completed", "expired"].includes(t.status)).length,
    completed: tests.filter(t => t.status === "test_completed").length,
    paid: tests.filter(t => t.payment_status === "completed").length
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="self-test-management">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Headphones className="w-7 h-7 text-indigo-600" />
              Test Sessions
            </h1>
            <p className="text-slate-500">Create and manage hearing test sessions for all scenarios</p>
          </div>
          
          <Button 
            onClick={() => setDialogOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Test Session
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-500 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-slate-600">Total Sessions</p>
                  <p className="text-xl font-bold text-slate-700">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-blue-600">Active</p>
                  <p className="text-xl font-bold text-blue-700">{stats.active}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-emerald-600">Completed</p>
                  <p className="text-xl font-bold text-emerald-700">{stats.completed}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-amber-600">Paid Tests</p>
                  <p className="text-xl font-bold text-amber-700">{stats.paid}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Scenario Filter Tabs */}
        <Tabs defaultValue="all">
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="in_clinic">In-Clinic</TabsTrigger>
            <TabsTrigger value="remote_link">Remote</TabsTrigger>
            <TabsTrigger value="self_test_paid">Self-Test</TabsTrigger>
            <TabsTrigger value="home_visit">Home Visit</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-4">
            <TestList 
              tests={tests} 
              formatStatus={formatStatus} 
              getScenarioIcon={getScenarioIcon}
              sendLink={sendLink}
              sending={sending}
              copyLink={copyLink}
              navigate={navigate}
            />
          </TabsContent>
          
          {["in_clinic", "remote_link", "self_test_paid", "home_visit"].map(scenario => (
            <TabsContent key={scenario} value={scenario} className="mt-4">
              <TestList 
                tests={tests.filter(t => t.scenario === scenario)} 
                formatStatus={formatStatus} 
                getScenarioIcon={getScenarioIcon}
                sendLink={sendLink}
                sending={sending}
                copyLink={copyLink}
                navigate={navigate}
              />
            </TabsContent>
          ))}
        </Tabs>

        {/* Create Test Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Test Session</DialogTitle>
              <DialogDescription>
                Set up a hearing test for a patient
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Scenario Selection */}
              <div>
                <Label className="mb-3 block">Test Scenario</Label>
                <div className="grid grid-cols-2 gap-3">
                  {SCENARIOS.map(scenario => {
                    const Icon = scenario.icon;
                    return (
                      <div
                        key={scenario.value}
                        onClick={() => setSelectedScenario(scenario.value)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedScenario === scenario.value 
                            ? "border-indigo-500 bg-indigo-50" 
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            selectedScenario === scenario.value ? "bg-indigo-500" : "bg-slate-100"
                          }`}>
                            <Icon className={`w-5 h-5 ${
                              selectedScenario === scenario.value ? "text-white" : "text-slate-500"
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{scenario.label}</p>
                            <p className="text-xs text-slate-500">{scenario.description}</p>
                          </div>
                        </div>
                        {scenario.paymentRequired && (
                          <Badge className="mt-2 bg-amber-100 text-amber-700">₹499 Payment</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Patient Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <Label>Patient Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Full name"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label>Mobile Number *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+91 98765 43210"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="patient@email.com"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Age</Label>
                    <Input
                      type="number"
                      value={formData.age}
                      onChange={(e) => setFormData({...formData, age: e.target.value})}
                      placeholder="45"
                    />
                  </div>
                  <div>
                    <Label>Gender</Label>
                    <Select value={formData.gender} onValueChange={(v) => setFormData({...formData, gender: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <Label>Notes (Optional)</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Any special instructions..."
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={createTest} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Create & Copy Link
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function TestList({ tests, formatStatus, getScenarioIcon, sendLink, sending, copyLink, navigate }) {
  if (tests.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Headphones className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No test sessions yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {tests.map(test => {
        const statusInfo = formatStatus(test.status);
        const ScenarioIcon = getScenarioIcon(test.scenario);
        const baseUrl = window.location.origin;
        const fullLink = `${baseUrl}/self-test/${test.test_code}`;
        
        return (
          <Card key={test.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <ScenarioIcon className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-900">{test.patient_name}</p>
                      <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                      {test.payment_required && test.payment_status === "completed" && (
                        <Badge className="bg-green-100 text-green-700">Paid</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                      <Phone className="w-3 h-3" />
                      {test.patient_phone}
                      <span className="text-slate-300">•</span>
                      <Clock className="w-3 h-3" />
                      {new Date(test.created_at).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Code: <code className="bg-slate-100 px-1 rounded">{test.test_code}</code>
                      {test.referred_by_name && (
                        <span> • Created by {test.referred_by_name}</span>
                      )}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {!["test_completed", "expired"].includes(test.status) && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyLink(fullLink)}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => sendLink(test.id, "sms")}
                        disabled={sending[test.id] === "sms"}
                      >
                        {sending[test.id] === "sms" ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <MessageSquare className="w-4 h-4 mr-1" />
                            SMS
                          </>
                        )}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => sendLink(test.id, "whatsapp")}
                        disabled={sending[test.id] === "whatsapp"}
                        className="text-green-600 border-green-200 hover:bg-green-50"
                      >
                        {sending[test.id] === "whatsapp" ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          "WhatsApp"
                        )}
                      </Button>
                    </>
                  )}
                  {test.pta_session_id && (
                    <Button 
                      size="sm"
                      onClick={() => navigate(`/pta-test/audiologist/${test.pta_session_id}`)}
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View Test
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
